import { Dispatch, MouseEventHandler, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button } from 'src/components/ui';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import Select, { components, Props, OptionProps, SingleValueProps } from 'react-select';
import UWEFormContext from '../../context/UWEFormContext';
import { extractFormFieldsFromSchema, FormField } from '../../util/extractFormFieldsFromSchema';
import Loader from '../../components/Loader';
import Notification from '../../components/Notification';
import MessageThread from '../MessageThread';
import { DataRemark, DataRemarkMessage } from '../../api/DataRemarksApi';
import { categoryKey, NewDataRemark, OTHER_SCHEDULE_KEY } from '../useDataRemarks';
import { CATEGORY_COLORS, getRemarkStatusKey, remarkStatus, renderValuesTable, STATUS_COLORS } from '../UWEEntityDataRemarks';
import { AccordionItem } from '../../components/ui/Accordion';
import CustomBadge from '../../components/CustomBadge';
import { AuthContext } from '../../context/AuthContext';
import { getTestId } from '../../hooks/useTestId';
import Icon from 'src/components/Icon';

const SingleValue = ({ children, ...props }: SingleValueProps<any>) => {
  const value = useMemo(() => props.selectProps.value as NewDataRemark, [props.selectProps.value]);

  const content = useMemo(() => {
    if (value == null) return children;

    if (value.fieldDescription) {
      return (
        <>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value.fieldDescription}</div>
          <small>{value.fieldTitle}</small>
        </>
      );
    }
    return value.fieldName;
  }, [children, value]);

  return <components.SingleValue {...props}>{content}</components.SingleValue>;
};

const Option = (props: OptionProps<FormField>) => {
  const { children, ...rest } = props;

  const content = useMemo(() => {
    if (rest.data.description) {
      return (
        <>
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rest.data.description}</div>
          <small>{rest.data.name}</small>
        </>
      );
    }
    return rest.data.name;
  }, [children, rest.data]);

  return <components.Option {...rest}>{content}</components.Option>;
};

const CustomSelect = (props: Props<FormField>) => {
  return (
    <Select
      {...props}
      components={{
        Option,
        SingleValue,
      }}
    />
  );
};

function DataRemarkModal({
  show,
  onClose,
  newRemark,
  setNewRemark,
  handleCreateRemark,
  isEditing = false,
  editingRemark = null,
  onAddMessage,
  onResolve,
  currentUserId,
  wfAccessToken,
  modalLoading = false,
  fieldsDisabled = false,
  error = null,
  getFieldValueForPath,
  availableFieldsByDataPath,
  availableFieldsByPath,
  mode,
}: {
  show: boolean;
  onClose: MouseEventHandler;
  newRemark: NewDataRemark;
  setNewRemark: Dispatch<SetStateAction<NewDataRemark>>;
  handleCreateRemark: MouseEventHandler;
  isEditing: boolean;
  editingRemark: { id?: number; category: string; messages?: DataRemarkMessage[]; createdBy?: { id?: number } | null; isResolved: boolean } | null;
  onAddMessage?: (payload: { message: string; file: File | null }) => Promise<void>;
  onResolve?: () => void;
  currentUserId?: number;
  wfAccessToken?: any;
  modalLoading: boolean;
  fieldsDisabled?: boolean;
  error:
    | {
        message?: string;
        result?: string;
      }
    | string
    | null;
  availableFieldsByPath: Record<string, FormField>;
  availableFieldsByDataPath: Record<string, FormField>;
  getFieldValueForPath: any;
  mode: string;
}) {
  const auth = useContext(AuthContext);
  const uweFormContext = useContext(UWEFormContext);
  const { subForms = {} } = uweFormContext || {};
  const errorMessage = error == null ? null : typeof error === 'string' ? error : error.message || error.result || null;
  const messages = editingRemark?.messages ?? [];

  const [isDirty, setIsDirty] = useState(false);
  const [areValuesOpened, setAreValuesOpened] = useState(false);

  const otherScheduleOption = useMemo(() => ({ value: OTHER_SCHEDULE_KEY, label: t`Other`, path: '' }), []);

  const subformOptions = useMemo(() => {
    const options = Object.keys(subForms).map(sfName => ({
      value: sfName,
      label: subForms[sfName].title,
      path: subForms[sfName].path as string,
    }));
    return [...options, otherScheduleOption];
  }, [subForms, otherScheduleOption]);

  const [currentSubForm, setCurrentSubForm] = useState<{ value: string; label: string; path: string } | null>();
  const [fieldOptions, setFieldOptions] = useState<FormField[]>([]);

  useEffect(() => {
    // If opening in edit mode...
    if (show && newRemark?.isGeneral) {
      setCurrentSubForm(otherScheduleOption);
    } else if (show && newRemark && newRemark.fieldPath) {
      const splitPath = newRemark.fieldPath.split('.');
      for (let i = 0; i < splitPath.length; i++) {
        const key = splitPath[i];
        const sf = subForms[key];
        if (sf != null) {
          setCurrentSubForm({ value: key, label: sf.title, path: sf.path });
        }
      }
    }
  }, [show]);

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setIsDirty(false);
    }
  }, [show]);

  const onSubformSelect = (value: { value: string; label: string } | null) => {
    if (value == null) {
      setCurrentSubForm(null);
      return;
    }
    if (currentSubForm === value) return;
    const isGeneral = value.value === OTHER_SCHEDULE_KEY;
    const sf = subForms[value.value];
    if (!isGeneral && !sf) return;
    setCurrentSubForm(isGeneral ? otherScheduleOption : { ...value, path: sf.path });
    setFieldOptions(isGeneral ? [] : extractFormFieldsFromSchema(sf.schema));
    // Reset new remark if subform changes
    setNewRemark({
      fieldTitle: '',
      fieldName: '',
      fieldPath: '',
      fieldDescription: '',
      description: '',
      message: '',
      _fields: [],
      isGeneral,
    });
  };

  let modalTitle;
  switch (true) {
    case editingRemark?.isResolved:
      modalTitle = <Trans>Resolved Flag</Trans>;
      break;
    case isEditing && fieldsDisabled:
      modalTitle = <Trans>Respond to Flag</Trans>;
      break;
    case isEditing:
      modalTitle = <Trans>Edit Flag</Trans>;
      break;
    default:
      modalTitle = <Trans>Add New Flag</Trans>;
  }

  const { label: statusLabel, key: statusKey } = remarkStatus(editingRemark as DataRemark, auth, mode);

  return (
    <Modal isOpen={show} toggle={onClose} size="lg" className="dataremark-modal-dialog" backdrop={false}>
      {modalLoading && (
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.1)',
            zIndex: 20,
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader spinner />
        </div>
      )}
      <ModalHeader toggle={onClose}>
        <span className="flex items-center gap-2">
          {modalTitle}
          {isEditing && !fieldsDisabled && onResolve && (
            <Button
              size="sm"
              color="success"
              className="flex items-center gap-1"
              onClick={onResolve}
              {...getTestId('data-remark-modal', '.', 'resolve')}
            >
              <Icon icon="Check" className="mr-1" />
              <Trans>Mark Resolved</Trans>
            </Button>
          )}
        </span>
      </ModalHeader>

      {isEditing ? (
        <ModalBody className="p-4">
          {errorMessage != null && <Notification color="danger">{errorMessage}</Notification>}

          {/* Compact read-only field info */}
          <div className="bg-light rounded mb-3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <small className="small-label">
                <Trans>Schedule</Trans>
              </small>
              <span className="small">{currentSubForm?.label || '--'}</span>
            </div>
            <div className="flex gap-2 items-center" style={{ justifyContent: 'end' }}>
              <CustomBadge color={CATEGORY_COLORS[categoryKey(editingRemark?.category)]} style={{ fontSize: '0.65rem' }}>
                {editingRemark?.category}
              </CustomBadge>
              <CustomBadge color={STATUS_COLORS[statusKey]} style={{ fontSize: '0.65rem' }} pill>
                {statusLabel}
              </CustomBadge>
            </div>
          </div>

          {editingRemark?.id && !newRemark.isGeneral && (
            <AccordionItem
              id={`dataremark-modal-values-${editingRemark?.id ?? 'new'}`}
              itemId={String(editingRemark?.id ?? 'new')}
              isOpen={areValuesOpened}
              toggle={() => setAreValuesOpened(prev => !prev)}
              className="mb-3"
              title={
                <span className="form-subtitle">
                  <Trans>Assosiated fields</Trans>
                </span>
              }
            >
              <div>{renderValuesTable(editingRemark as DataRemark, getFieldValueForPath, availableFieldsByDataPath, availableFieldsByPath)}</div>
            </AccordionItem>
          )}

          <div className="flex flex-col gap-2">
            {fieldsDisabled ? (
              <div className="bg-light rounded" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <small className="small-label">
                    <Trans>Issue Summary</Trans>
                  </small>
                  <span className="small block">{newRemark.message || '--'}</span>
                </div>
                {newRemark.description && (
                  <div>
                    <small className="small-label">
                      <Trans>Detailed Description</Trans>
                    </small>
                    <span className="small block">{newRemark.description}</span>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div>
                  <Label for="message" className="small-label">
                    <Trans>Issue Summary</Trans> <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="message"
                    value={newRemark.message}
                    onChange={e => {
                      setNewRemark(prev => ({ ...prev, message: e.target.value }));
                      setIsDirty(true);
                    }}
                    placeholder={t`Brief description of the issue`}
                    required
                    {...getTestId('data-remark-modal', '.', 'message')}
                  />
                </div>
                <div>
                  <Label for="description" className="small-label">
                    <Trans>Detailed Description</Trans>
                  </Label>
                  <Input
                    type="textarea"
                    id="description"
                    value={newRemark.description}
                    onChange={e => {
                      setNewRemark(prev => ({ ...prev, description: e.target.value }));
                      setIsDirty(true);
                    }}
                    placeholder={t`Provide detailed information about the issue`}
                    rows={3}
                    {...getTestId('data-remark-modal', '.', 'description')}
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    color="primary"
                    onClick={handleCreateRemark}
                    disabled={!isDirty}
                    className="flex items-center gap-1"
                    {...getTestId('data-remark-modal', '.', 'update')}
                  >
                    <Trans>Update Flag</Trans>
                  </Button>
                </div>
              </>
            )}
          </div>

          {!(editingRemark?.isResolved && messages.length === 0) && (
            <div className="mt-3">
              <MessageThread
                messages={messages}
                currentUserId={currentUserId}
                remarkCreatedById={editingRemark?.createdBy?.id ?? null}
                responseMode={fieldsDisabled}
                wfAccessToken={wfAccessToken}
                onSend={onAddMessage}
                isOpen={show}
              />
            </div>
          )}
        </ModalBody>
      ) : (
        <ModalBody>
          {errorMessage != null && <Notification color="danger">{errorMessage}</Notification>}
          <Form>
            <FormGroup>
              <Label for="subformSelect" className="small-label">
                <Trans>Schedule</Trans> <span className="text-danger">*</span>
              </Label>
              <Select
                id="subformSelect"
                value={currentSubForm}
                onChange={value => onSubformSelect(value)}
                isDisabled={isEditing}
                options={subformOptions}
                isSearchable
                placeholder={t`-- Select a schedule --`}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </FormGroup>

            <FormGroup hidden={newRemark.isGeneral}>
              <Label for="fieldSelect" className="small-label">
                <Trans>Form Field</Trans> <span className="text-danger">*</span>
              </Label>
              <CustomSelect
                id="fieldSelect"
                isDisabled={currentSubForm == null}
                value={(newRemark?.fieldPath ? newRemark : undefined) as FormField | undefined}
                onChange={(selected: any) => {
                  if (selected) {
                    const name = selected.description ? `${selected.description} (${selected.name})` : selected.name;
                    const path = currentSubForm ? `${currentSubForm.path}.${selected.path}` : selected.path;
                    // Store the actual data path (matching Cross-Schedule/Red Flag/Amendment
                    // remarks), not the schema-tree path — they can diverge whenever a field's
                    // map:field differs from its schema key, which broke matching/dedup against
                    // auto-generated remarks for the same field. availableFieldsByPath already
                    // resolves the schema path to the correctly map:field-prefixed dataPath the
                    // same way the rest of the form (navigation, value lookups) does.
                    const dataPath = availableFieldsByPath?.[path]?.dataPath ?? path;
                    setNewRemark(prev => ({
                      ...prev,
                      fieldName: name,
                      fieldPath: dataPath,
                      fieldDescription: selected.description ?? '',
                      fieldTitle: selected.name,
                      _fields: [{ name, path: dataPath }],
                    }));
                  } else {
                    setNewRemark(prev => ({
                      ...prev,
                      fieldName: '',
                      fieldPath: '',
                      fieldId: null,
                      _fields: [],
                    }));
                  }
                }}
                options={fieldOptions}
                getOptionLabel={o => `${o.name} ${o.description}`}
                getOptionValue={o => (o as FormField).id}
                isClearable
                isSearchable
                placeholder={t`-- Select a form field --`}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </FormGroup>

            <FormGroup>
              <Label for="message" className="small-label">
                <Trans>Issue Summary</Trans> <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                id="message"
                value={newRemark.message}
                onChange={e => setNewRemark(prev => ({ ...prev, message: e.target.value }))}
                placeholder={t`Brief description of the issue`}
                required
                {...getTestId('data-remark-modal', '.', 'message')}
              />
            </FormGroup>

            <FormGroup>
              <Label for="description" className="small-label">
                <Trans>Detailed Description</Trans>
              </Label>
              <Input
                type="textarea"
                id="description"
                value={newRemark.description}
                onChange={e => setNewRemark(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t`Provide detailed information about the issue`}
                rows={3}
                {...getTestId('data-remark-modal', '.', 'description')}
              />
            </FormGroup>
          </Form>
        </ModalBody>
      )}

      {!isEditing && (
        <ModalFooter>
          <Button color="secondary" onClick={onClose} {...getTestId('data-remark-modal', '.', 'cancel')}>
            <Trans>Cancel</Trans>
          </Button>
          <Button color="primary" onClick={handleCreateRemark} className="flex items-center gap-1" {...getTestId('data-remark-modal', '.', 'create')}>
            <Trans>Add Flag</Trans>
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
}

export default DataRemarkModal;
